import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Shell } from "@/components/Shell";
import { ProgressBar } from "@/components/ProgressBar";
import { QuestionField } from "@/components/QuestionField";
import { getSections, visibleQuestions } from "@/data/questionnaire";
import { getSubmission, upsertSubmission } from "@/lib/storage";

export default function SurveyPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [sub, setSub] = useState(() => getSubmission(id));
  const fromSummary = params.get("from") === "riepilogo";
  const [idx, setIdx] = useState(() => {
    const p = params.get("s");
    return p !== null ? Number(p) : sub?.sectionIndex || 0;
  });

  useEffect(() => {
    if (!sub) navigate("/", { replace: true });
  }, [sub, navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [idx]);

  if (!sub) return null;
  const SECTIONS = getSections(sub.businessType);
  const section = SECTIONS[idx];
  const questions = visibleQuestions(section, sub.answers);
  const isLast = idx === SECTIONS.length - 1;

  const setAnswer = (qid, v) => {
    const next = { ...sub, answers: { ...sub.answers, [qid]: v }, sectionIndex: idx };
    setSub(upsertSubmission(next));
  };

  const go = (n) => {
    upsertSubmission({ ...sub, sectionIndex: n });
    setIdx(n);
  };

  const next = () => (isLast || fromSummary ? navigate(`/riepilogo/${id}`) : go(idx + 1));

  return (
    <Shell header={<span className="text-sm text-[#7A6B63]" data-testid="survey-respondent">{sub.respondent}</span>}>
      <ProgressBar current={idx} total={SECTIONS.length} />
      <div key={section.id} className="fade-up">
        <p className="eyebrow">Sezione {idx + 1}</p>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#2C221E] mt-2" data-testid="section-title">{section.title}</h1>
        <p className="text-[#5C4D46] mt-3 text-base sm:text-lg">{section.intro}</p>

        <div className="mt-10 grid gap-10">
          {questions.map((q, i) => (
            <div key={q.id} className="card p-6 sm:p-8" data-testid={`question-${q.id}`}>
              <div className="flex gap-4 items-start mb-5">
                <span className="flex-none font-serif text-2xl font-semibold text-[#C85A32] leading-none mt-0.5">{i + 1}</span>
                <div>
                  <p className="text-lg sm:text-xl font-semibold text-[#2C221E] leading-snug">{q.text}</p>
                  {q.hint && <p className="text-sm text-[#7A6B63] mt-1">{q.hint}</p>}
                </div>
              </div>
              <QuestionField q={q} value={sub.answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 justify-between mt-10">
          <button className="btn btn-ghost" onClick={() => (idx === 0 ? navigate("/nuovo") : go(idx - 1))} data-testid="prev-section-btn">
            <ArrowLeft size={18} /> Indietro
          </button>
          <button className="btn btn-primary" onClick={next} data-testid="next-section-btn">
            {isLast || fromSummary ? "Vai al riepilogo" : "Avanti"} <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </Shell>
  );
}
